import type { UserProfile, UserRepository } from './RepositoryInterfaces.js';

const USERS_STORAGE_KEY = 'k-ucap-inmemory-users';

export class InMemoryUserRepository implements UserRepository {
  private users: Map<string, UserProfile> = new Map();

  constructor() {
    this.loadFromStorage();
  }

  private loadFromStorage(): void {
    try {
      const raw = localStorage.getItem(USERS_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Record<string, UserProfile>;
        this.users = new Map(Object.entries(parsed));
      }
    } catch {
      // ignore storage errors
    }
  }

  private persist(): void {
    try {
      localStorage.setItem(USERS_STORAGE_KEY, JSON.stringify(Object.fromEntries(this.users)));
    } catch {
      // ignore storage errors
    }
  }

  async findById(uid: string): Promise<UserProfile | undefined> {
    return this.users.get(uid);
  }

  async findByEmail(email: string): Promise<UserProfile | undefined> {
    return Array.from(this.users.values()).find((u) => u.email === email);
  }

  async findAll(): Promise<UserProfile[]> {
    return Array.from(this.users.values());
  }

  async save(profile: UserProfile): Promise<UserProfile> {
    this.users.set(profile.uid, profile);
    this.persist();
    return profile;
  }

  async ensureDefaultProfile(
    authUser: { uid: string; email: string | null; displayName: string | null; photoURL: string | null },
    authProvider: 'email' | 'google' | 'mock'
  ): Promise<UserProfile> {
    const existing = await this.findById(authUser.uid);
    if (existing) return existing;
    const { GlobalRole, UserStatus } = await import('@k-ucap/shared');
    const now = new Date().toISOString();
    const profile: UserProfile = {
      uid: authUser.uid,
      email: authUser.email ?? '',
      displayName: authUser.displayName ?? authUser.email ?? 'Unknown',
      photoURL: authUser.photoURL ?? undefined,
      globalRole: GlobalRole.GUEST,
      status: UserStatus.ACTIVE,
      authProvider,
      environment: 'development',
      lastLoginAt: now,
      createdAt: now,
      updatedAt: now,
    };
    await this.save(profile);
    return profile;
  }

  async updateDefaultProject(uid: string, defaultProjectId: string): Promise<void> {
    const user = this.users.get(uid);
    if (user) this.users.set(uid, { ...user, defaultProjectId });
  }

  seed(users: UserProfile[]): void {
    users.forEach((u) => this.users.set(u.uid, u));
    this.persist();
  }
}

export const createInMemoryUserRepository = (): InMemoryUserRepository => new InMemoryUserRepository();
